import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, Link, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { retrievePhoto } from '../../../api/getPhotoDocumentsApi';
import { getAuthKey, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import placeholderImage from '../../../assets/images/greyPlaceHolder.png'; // Adjust the path as necessary
import { PhotoInfo } from '../../../types/type';
import { getPhotoDate, truncateFileName } from '../../../utils/Utilities';

interface ClaimDetailPhotosType {
  photoInfo: PhotoInfo;
}

const DIALOG_MARGIN = 20;
const MIN_PHOTO_HEIGHT = 150;
// Matches the global modal offsets in styles.scss, which clear the parent app's sticky header
const PARENT_HEADER_OFFSET = { xs: 100, sm: 120, md: 150 };

export default function ClaimDetailIndividualPhoto(props: ClaimDetailPhotosType) {
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const [source, setSource] = useState(placeholderImage);
  const [open, setOpen] = useState(false);
  const [dialogTop, setDialogTop] = useState(DIALOG_MARGIN);
  const [photoMaxHeight, setPhotoMaxHeight] = useState<number>();
  const availableHeightRef = useRef(0);
  const paperRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);
  const theme = useTheme();

  const imagePreviewStyle = {
    maxWidth: '140px',
    maxHeight: '120px',
    width: 'auto',
    height: 'auto',
    cursor: 'pointer',
  };

  useEffect(() => {
    const getPhoto = async () => {
      const response = await retrievePhoto(props.photoInfo.url, props.photoInfo.mimeType, urlKey, sessionId, authKey);
      setSource(response);
    };
    getPhoto();
  }, [props.photoInfo, urlKey, authKey]);

  const getParentHeaderOffset = () => {
    if (window.innerWidth >= theme.breakpoints.values.md) {
      return PARENT_HEADER_OFFSET.md;
    }
    return window.innerWidth >= theme.breakpoints.values.sm ? PARENT_HEADER_OFFSET.sm : PARENT_HEADER_OFFSET.xs;
  };

  const handleClickOpen = (event: React.MouseEvent) => {
    event.preventDefault();
    // When embedded, the iframe is sized to its content, so a fixed dialog spans the full iframe height
    // rather than the user's screen. Ask the browser which part of this document is actually visible
    // (accounting for the parent page's scroll position) and open the dialog there.
    const observer = new IntersectionObserver(([entry]) => {
      observer.disconnect();
      const visible = entry.intersectionRect;
      // A positive top means the iframe is scrolled up past the top of the screen, where the parent
      // app's sticky header overlaps it. The observer can't see that header, so leave room for it.
      const topOffset = visible.top > 0 ? getParentHeaderOffset() : DIALOG_MARGIN;
      availableHeightRef.current = visible.height - topOffset - DIALOG_MARGIN;
      setDialogTop(visible.top + topOffset);
      setOpen(true);
    });
    observer.observe(document.documentElement);
  };

  // Runs once the dialog is laid out but before it is visible. The title, date and padding around
  // the photo vary by theme and the user's font settings, so measure them and give the photo the rest.
  const fitPhotoToVisibleArea = () => {
    if (!paperRef.current || !photoRef.current) {
      return;
    }
    const surroundingHeight = paperRef.current.offsetHeight - photoRef.current.offsetHeight;
    setPhotoMaxHeight(Math.max(MIN_PHOTO_HEIGHT, availableHeightRef.current - surroundingHeight));
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={6}>
          <img
            className="imagePreviewStyle"
            src={source}
            alt="uploaded_Photo"
            style={imagePreviewStyle}
            onClick={handleClickOpen}
          />
        </Grid>
        <Grid sx={{ px: '1em' }} item xs={6}>
          <Link underline="none" sx={theme.custom.styles.phoneNumberAndWebSiteStyle} onClick={handleClickOpen}>
            {truncateFileName(_get(props, 'photoInfo.description', ''))}
          </Link>
          <Typography variant="body1">{getPhotoDate(_get(props, 'photoInfo.dateCreated', ''))}</Typography>
        </Grid>
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="body"
        PaperProps={{ ref: paperRef }}
        TransitionProps={{ onEnter: fitPhotoToVisibleArea }}
        sx={{
          // Override the global modal offset in styles.scss; this dialog positions itself in the visible area
          '&.MuiModal-root': {
            paddingTop: '0 !important',
          },
          '& .MuiDialog-paper': {
            verticalAlign: 'top',
            marginTop: `${dialogTop}px`,
            marginBottom: `${DIALOG_MARGIN}px`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {truncateFileName(_get(props, 'photoInfo.description', ''))}
          <IconButton onClick={handleClose} color="primary" sx={{ px: '0rem' }} disableRipple>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Typography variant="body1" style={{ marginLeft: '1.5rem' }}>
          {getPhotoDate(_get(props, 'photoInfo.dateCreated', ''))}
        </Typography>
        <DialogContent>
          <img
            ref={photoRef}
            src={source}
            alt="uploaded_Photo"
            style={{
              display: 'block',
              margin: '0 auto',
              maxWidth: '100%',
              maxHeight: photoMaxHeight ? `${photoMaxHeight}px` : undefined,
              width: 'auto',
              height: 'auto',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
